import { useCallback, useEffect, useState } from 'react';
import type { Scope, SpraypaintBase } from 'spraypaint';
import { reportError } from '../utilities/reportError';

interface IJsonApiError {
  status?: string;
  code?: string;
  title?: string;
  detail?: string;
  meta?: Record<string, any>;
}

export interface IJsonApiErrorResponse {
  errors: IJsonApiError[];
}

export function useSpraypaintFind<TModel = any, TMeta = any>(
  model: any,
  id: number | string,
  initialMeta: TMeta = null,
  initialExtraParams: Record<string, any> | undefined = null,
) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<IJsonApiErrorResponse>();
  const [data, setData] = useState<TModel>(null);
  const [meta, setMeta] = useState<TMeta>(initialMeta);
  const [extraParams, setExtraParams] = useState(initialExtraParams);

  let scope = (model as Scope).scope();
  if (extraParams) scope = scope.extraParams(extraParams);

  useEffect(() => {
    fetchData();
  }, [id, extraParams]);

  function fetchData() {
    if (!id) {
      setIsLoading(false);
      return Promise.resolve();
    }

    setIsLoading(true);

    return scope
      .find(id)
      .then(({ data: spraypaintData, meta: spraypaintMeta }) => {
        setError(null);
        setData(spraypaintData as unknown as TModel);
        setMeta(spraypaintMeta as TMeta);
        return spraypaintData as unknown as TModel;
      })
      .catch((responseError: { response: Response; originalError: any }) => {
        reportError(responseError);
        responseError.response
          .clone()
          .json()
          .then((data: IJsonApiErrorResponse) => {
            setError(data);
          });
      })
      .then((passThrough) => {
        setIsLoading(false);
        setIsInitialLoading(false);
        return passThrough;
      });
  }

  return {
    data,
    error,
    extraParams,
    fetchData,
    isInitialLoading,
    isLoading,
    meta,
    scope,
    setExtraParams,
  };
}

export const useSpraypaintFindNamedParams = <TModel extends SpraypaintBase, TMeta = unknown>({
  scope,
  id,
  initialMeta,
  initialExtraParams = {},
}: {
  scope: Scope<TModel>;
  id: number | string;
  initialMeta?: TMeta;
  initialExtraParams?: Record<string, unknown>;
}) => useSpraypaintFind<TModel, TMeta>(scope, id, initialMeta, initialExtraParams);

export function useSpraypaintFindWithFallback<
  TModel extends SpraypaintBase,
  TMeta = any,
  TScope extends Scope<TModel> = Scope<TModel>,
>({
  scope,
  id,
  initialMeta = null,
  initialExtraParams = null,
  fallback,
}: {
  scope: TScope;
  id: number | string;
  fallback: TModel;
  initialMeta?: TMeta;
  initialExtraParams?: Record<string, any> | undefined;
}) {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<IJsonApiErrorResponse>();
  const [data, setData] = useState<TModel>(null);
  const [meta, setMeta] = useState<TMeta>(initialMeta);
  const [extraParams, setExtraParams] = useState(initialExtraParams);

  // we will use the scope from the first render only for simplicity
  // you may get weird issues if changing the scope
  const [firstRenderScope] = useState(scope);

  const fetchData = useCallback(() => {
    let internalScope = firstRenderScope.scope();
    if (extraParams) internalScope = internalScope.extraParams(extraParams);

    if (!id) {
      setIsLoading(false);
      setData(fallback);
      return Promise.resolve();
    }

    setIsLoading(true);

    return internalScope
      .find(id)
      .then(({ data: spraypaintData, meta: spraypaintMeta }) => {
        setError(null);
        setData(spraypaintData);
        setMeta(spraypaintMeta as TMeta);
        return spraypaintData;
      })
      .catch((responseError: { response: Response; originalError: any }) => {
        reportError(responseError);
        responseError.response
          .clone()
          .json()
          .then((data: IJsonApiErrorResponse) => {
            setError(data);
          });
      })
      .then((passThrough) => {
        setIsLoading(false);
        setIsInitialLoading(false);
        return passThrough;
      })
      .finally(() => {
        setData((prev) => {
          if (prev) return prev;
          return fallback;
        });
      });
  }, [extraParams, firstRenderScope, id, fallback]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    error,
    extraParams,
    fetchData,
    isInitialLoading,
    isLoading,
    meta,
    scope,
    setExtraParams,
  };
}

export default useSpraypaintFind;
